import {
  Activity,
  ArrowRight,
  BarChart3,
  BookOpen,
  Boxes,
  BrainCircuit,
  Check,
  ChevronRight,
  CircleGauge,
  ClipboardCheck,
  Clock3,
  ExternalLink,
  FileCheck2,
  Flag,
  Globe2,
  Languages,
  Layers3,
  LockKeyhole,
  Mail,
  Menu,
  Network,
  Phone,
  Plus,
  Rocket,
  Route,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UserCheck,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import {
  Link,
  NavLink,
  Route as BrowserRoute,
  Routes,
  useNavigate,
} from "react-router-dom";
import type {
  BootstrapResponse,
  Evidence,
  LeadCreateRequest,
  Language,
  Mission,
  MissionResponse,
  SalesOutcomeType,
  SalesPlaybook,
  SellerAccount,
} from "../shared/types";
import {
  approveTask,
  createLead,
  createMission,
  executeTask,
  getBootstrap,
  getMission,
  importLeads,
  recordSalesOutcome,
  rollbackTask,
  sendCustomerReply,
  syncCrmLead,
  updateTaskProposal,
} from "./api";
import { t } from "./i18n";

interface LanguageProps {
  language: Language;
}

interface HeaderProps extends LanguageProps {
  onLanguageChange: (language: Language) => void;
}

function defaultObjective(account: SellerAccount, language: Language, playbook: SalesPlaybook) {
  const action = {
    qualify: language === "zh" ? "判断销售机会并给出资格建议" : "qualify the opportunity and recommend the next sales step",
    first_touch: language === "zh" ? "生成个性化首次触达并安排跟进" : "create a personalized first touch and follow-up",
    reactivate: language === "zh" ? "重新激活停滞的卖家机会" : "reactivate the stalled seller opportunity",
    launch_rescue: language === "zh" ? "保护首发承诺并协调排障会议" : "protect the launch commitment and coordinate an unblock meeting",
  }[playbook];
  return language === "zh"
    ? `为 ${account.name} ${action}。`
    : `${action[0].toUpperCase()}${action.slice(1)} for ${account.name}.`;
}

function recommendedPlaybookForAccount(accountId: string): SalesPlaybook {
  return accountId === "brightcup-cn-us"
    ? "launch_rescue"
    : accountId === "harbornest-cn-us"
      ? "reactivate"
      : "first_touch";
}

type LifecycleStage = "Acquisition" | "Launch" | "Growth";

function lifecycleStageForAccount(account: {
  stage: string;
  journeyMilestones?: SellerAccount["journeyMilestones"];
}): LifecycleStage {
  const normalized = account.stage.toLowerCase();
  if (
    normalized.includes("activated") ||
    normalized.includes("growth") ||
    normalized.includes("first sale") ||
    account.journeyMilestones?.find((milestone) => milestone.id === "first_sale")
      ?.status === "complete"
  ) {
    return "Growth";
  }
  if (
    normalized.includes("lead") ||
    normalized.includes("qualified") ||
    normalized.includes("meeting") ||
    normalized.includes("contacted") ||
    normalized.includes("nurture")
  ) {
    return "Acquisition";
  }
  if (
    normalized.includes("registered") ||
    normalized.includes("launch") ||
    normalized.includes("compliance") ||
    normalized.includes("selection")
  ) {
    return "Launch";
  }
  return "Acquisition";
}

function parseLeadCsv(value: string): LeadCreateRequest[] {
  const result = Papa.parse<Record<string, string>>(value, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (header) => header.trim().toLowerCase(),
  });
  if (result.errors.length) return [];
  return result.data.map((row) => ({
    name: row.name?.trim() ?? "",
    category: row.category?.trim() ?? "",
    contactName: row.contactname?.trim() ?? "",
    contactRole: row.contactrole?.trim() ?? "",
    leadSource: row.leadsource?.trim() ?? "",
    buyingSignal: row.buyingsignal?.trim() ?? "",
    painPoint: row.painpoint?.trim() ?? "",
    owner: row.owner?.trim() ?? "",
    contactability:
      (row.contactability?.trim() as LeadCreateRequest["contactability"]) ||
      "unknown",
  }));
}

function OmarMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand-mark" aria-label="Omar">
      <span className="brand-glyph" aria-hidden="true">
        O
      </span>
      {!compact && (
        <span className="brand-copy">
          <strong>OMAR</strong>
          <small>Opportunity · Mobilize · Approve · Realize</small>
        </span>
      )}
    </div>
  );
}

function Header({ language, onLanguageChange }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const labels = t(language);
  const links = [
    ["/dashboard", labels.nav.dashboard],
    ["/workspace", labels.nav.workspace],
  ];

  return (
    <header className="site-header">
      <Link to="/" className="brand-link" aria-label="Omar home">
        <OmarMark />
      </Link>
      <button
        className="mobile-menu"
        type="button"
        aria-label="Toggle navigation"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        {menuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      <nav className={menuOpen ? "nav-links is-open" : "nav-links"} aria-label="Primary navigation">
        {links.map(([href, label]) => (
          <NavLink
            key={href}
            to={href}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="header-actions">
        <div className="region-pill" title="Demo region route">
          <Globe2 size={14} />
          <span>CN</span>
          <ChevronRight size={12} />
          <strong>US</strong>
        </div>
        <button
          className="language-toggle"
          type="button"
          onClick={() => onLanguageChange(language === "en" ? "zh" : "en")}
          aria-label={language === "en" ? "切换到中文" : "Switch to English"}
        >
          <Languages size={16} />
          {language === "en" ? "中文" : "EN"}
        </button>
      </div>
    </header>
  );
}

function LandingPage({ language }: LanguageProps) {
  const isZh = language === "zh";
  return (
    <main>
      <section className="hero">
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-copy">
          <div className="eyebrow">
            <Sparkles size={14} />
            {isZh ? "从招募到首单的卖家旅程 Agent" : "Lead-to-first-sale seller journey agent"}
          </div>
          <h1>
            {isZh ? (
              <>
                把有限的人力投入
                <span>最值得推进的卖家。</span>
              </>
            ) : (
              <>
                Put scarce seller-facing time
                <span>where it moves the funnel.</span>
              </>
            )}
          </h1>
          <p>
            {isZh
              ? "Omar 找出最可能停滞、最值得人工介入的卖家，协调销售、注册、合规和营销动作，并验证这次介入是否推动了注册或首发里程碑。"
              : "Omar identifies sellers most likely to stall and most worth human intervention, coordinates the approved next action, and verifies whether it moved registration or launch."}
          </p>
          <div className="hero-actions">
            <Link className="button button-primary" to="/workspace">
              {t(language).common.openWorkspace}
              <ArrowRight size={17} />
            </Link>
            <Link className="button button-ghost" to="/dashboard">
              {t(language).common.learnSystem}
            </Link>
          </div>
          <div className="trust-row">
            <span><ShieldCheck size={15} /> {isZh ? "人工审批" : "Human approval"}</span>
            <span><BookOpen size={15} /> {isZh ? "证据可追溯" : "Evidence traceability"}</span>
            <span><LockKeyhole size={15} /> {isZh ? "安全模拟执行" : "Safe mock execution"}</span>
          </div>
        </div>

        <div className="hero-console" aria-label="Omar mission preview">
          <div className="console-header">
            <div>
              <span className="live-dot" />
              {isZh ? "任务运行中" : "Mission in progress"}
            </div>
            <span>CN → US</span>
          </div>
          <div className="console-account">
            <div className="account-avatar">BC</div>
            <div>
              <strong>BrightCup</strong>
              <small>{isZh ? "消费电子 · 美国首发准备" : "Consumer electronics · US pre-launch"}</small>
            </div>
            <div className="health-score">
              <span>92</span>
              <small>{isZh ? "优先级" : "priority"}</small>
            </div>
          </div>
          <div className="signal-strip">
            <div>
              <small>{isZh ? "里程碑" : "Milestones"}</small>
              <strong>2 / 5</strong>
              <span className="down">{isZh ? "3 项待完成" : "3 remaining"}</span>
            </div>
            <div>
              <small>{isZh ? "活动锁定" : "Campaign lock"}</small>
              <strong>12d</strong>
              <span className="down">{isZh ? "紧急" : "urgent"}</span>
            </div>
            <div>
              <small>{isZh ? "材料缺口" : "Evidence gaps"}</small>
              <strong>2</strong>
              <span className="down">{isZh ? "需人工验证" : "human review"}</span>
            </div>
          </div>
          <div className="agent-loop">
            {[
              [BrainCircuit, isZh ? "排序" : "Prioritize", isZh ? "组合第一" : "#1 in portfolio"],
              [Route, isZh ? "协调" : "Coordinate", isZh ? "跨团队计划" : "Cross-team plan"],
              [UserCheck, isZh ? "审批" : "Approve", isZh ? "等待人工" : "Human gate"],
              [FileCheck2, isZh ? "验证" : "Verify", isZh ? "全程审计" : "Full audit"],
            ].map(([Icon, title, note], index) => {
              const LoopIcon = Icon as typeof BrainCircuit;
              return (
                <div className={index < 2 ? "loop-step complete" : "loop-step"} key={String(title)}>
                  <span><LoopIcon size={16} /></span>
                  <strong>{String(title)}</strong>
                  <small>{String(note)}</small>
                </div>
              );
            })}
          </div>
          <div className="approval-preview">
            <div className="approval-icon"><LockKeyhole size={18} /></div>
            <div>
              <strong>{isZh ? "保护父亲节首发" : "Protect Father's Day launch"}</strong>
              <small>{isZh ? "CRM 跟进 + 可逆沙盒暂停" : "CRM follow-up + reversible sandbox hold"}</small>
            </div>
            <span>{isZh ? "需审批" : "Approval"}</span>
          </div>
        </div>
      </section>

      <section className="outcome-band">
        <div>
          <span>01</span>
          <strong>{isZh ? "减少漏斗流失" : "Reduce funnel leakage"}</strong>
          <p>{isZh ? "发现卡在资格判断、注册验证和首发准备中的卖家。" : "Find sellers stalled across qualification, verification, and launch."}</p>
        </div>
        <div>
          <span>02</span>
          <strong>{isZh ? "提升一线覆盖能力" : "Expand frontline capacity"}</strong>
          <p>{isZh ? "把研究、准备、CRM 更新和跟进安排交给 Agent。" : "Offload research, preparation, CRM updates, and follow-up scheduling."}</p>
        </div>
        <div>
          <span>03</span>
          <strong>{isZh ? "归因人工介入" : "Attribute human intervention"}</strong>
          <p>{isZh ? "连接“采取了什么动作”和“卖家是否进入下一阶段”。" : "Connect the approved action to verified seller-stage movement."}</p>
        </div>
      </section>

      <section className="principles-section">
        <div className="section-kicker">{isZh ? "产品原则" : "Product principles"}</div>
        <div className="section-heading">
          <h2>{isZh ? "为复杂的全球增长工作而设计。" : "Built for the messy work of global growth."}</h2>
          <p>
            {isZh
              ? "Omar 不是一个聊天窗口，而是一套将信号转化为有负责人、有证据、有控制的增长任务系统。"
              : "Omar is not another seller chatbot. It is the employee-facing orchestration layer across the acquisition, launch, adoption, and growth journey."}
          </p>
        </div>
        <div className="principle-grid">
          {[
            [Target, isZh ? "客户倒推" : "Work backward", isZh ? "从卖家目标和业务结果出发，而不是从模型能力出发。" : "Start with seller outcomes, not model capabilities."],
            [CircleGauge, isZh ? "经营机制" : "Operating mechanism", isZh ? "把洞察变成任务、审批、执行和复盘。" : "Turn insight into plans, approvals, actions, and review."],
            [ShieldCheck, isZh ? "默认负责" : "Responsible by default", isZh ? "来源白名单、提示注入防护和人工决策权。" : "Allowlisted sources, injection defenses, and human agency."],
            [Globe2, isZh ? "区域可扩展" : "Region-extensible", isZh ? "区域策略和证据解耦，支持更多跨境路径。" : "Decouple regional strategy and evidence for new corridors."],
          ].map(([Icon, title, detail]) => {
            const PrincipleIcon = Icon as typeof Target;
            return (
              <article className="principle-card" key={String(title)}>
                <PrincipleIcon size={21} />
                <h3>{String(title)}</h3>
                <p>{String(detail)}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="disclaimer-section">
        <div>
          <Flag size={20} />
          <strong>{isZh ? "独立作品集项目" : "Independent portfolio product"}</strong>
        </div>
        <p>
          {isZh
            ? "Omar 与 Amazon 无关联，也不代表 Amazon。演示账户和经营数据均为虚构；引用仅来自公开来源，所有执行仅发生在安全模拟连接器中。"
            : "Omar is not affiliated with or endorsed by Amazon. The seller and operating data are fictional; citations use public sources, and every action executes only in a safe mock connector."}
        </p>
      </section>
    </main>
  );
}

function MetricCard({ metric }: { metric: SellerAccount["metrics"][number] }) {
  const positive = metric.status === "positive";
  return (
    <article className={`metric-card ${metric.status}`}>
      <div className="metric-label">
        <span>{metric.label}</span>
        <Activity size={14} />
      </div>
      <strong>{metric.value}</strong>
      <small className={positive ? "positive-text" : "negative-text"}>
        {metric.change > 0 ? "↑" : "↓"} {Math.abs(metric.change)}% vs prior period
      </small>
    </article>
  );
}

function PortfolioRadar({
  bootstrap,
  language,
  selectedId,
  onSelect,
  onAddLead,
}: {
  bootstrap: BootstrapResponse;
  language: Language;
  selectedId: string;
  onSelect: (accountId: string) => void;
  onAddLead: () => void;
}) {
  const isZh = language === "zh";
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "hot" | "sla">("all");
  const visibleSellers = bootstrap.portfolio.filter((seller) => {
    const matchesQuery = [seller.name, seller.contactName, seller.owner, seller.stage]
      .join(" ")
      .toLowerCase()
      .includes(query.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "hot" && seller.leadTemperature === "hot") ||
      (filter === "sla" && new Date(seller.slaDueAt).getTime() < Date.now());
    return matchesQuery && matchesFilter;
  });
  return (
    <aside className="portfolio-radar">
      <div className="panel-heading">
        <div>
          <span className="section-kicker">{isZh ? "销售管道" : "Sales pipeline"}</span>
          <h3>{isZh ? "下一批卖家机会" : "Seller opportunities"}</h3>
        </div>
        <button className="add-lead-button" type="button" onClick={onAddLead}>
          <Plus size={13} /> {isZh ? "线索" : "Lead"}
        </button>
      </div>
      <p className="radar-subtitle">
        {isZh ? "综合承诺、风险、意向和里程碑的合成排序。" : "Synthetic ranking across commitments, risk, intent, and milestones."}
      </p>
      <label className="pipeline-search">
        <Search size={15} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={isZh ? "搜索公司、联系人或负责人" : "Search company, contact, or owner"}
        />
      </label>
      <div className="pipeline-filters">
        {[
          ["all", isZh ? "全部" : "All"],
          ["hot", isZh ? "高意向" : "Hot"],
          ["sla", isZh ? "SLA 风险" : "SLA risk"],
        ].map(([id, label]) => (
          <button
            type="button"
            className={filter === id ? "selected" : ""}
            key={id}
            onClick={() => setFilter(id as typeof filter)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="seller-rank-list">
        {visibleSellers.map((seller) => {
          const index = bootstrap.portfolio.findIndex((item) => item.id === seller.id);
          const slaAtRisk = new Date(seller.slaDueAt).getTime() < Date.now();
          const lifecycle = lifecycleStageForAccount(seller);
          return (
          <button
            className={seller.id === selectedId ? "selected" : ""}
            key={seller.id}
            type="button"
            onClick={() => onSelect(seller.id)}
          >
            <div className="seller-rank">{index + 1}</div>
            <div className="seller-summary">
              <div><strong>{seller.name}</strong><span>{seller.priorityScore}</span></div>
              <div className="seller-stage-row">
                <span className={`lifecycle-badge ${lifecycle.toLowerCase()}`}>
                  {lifecycle}
                </span>
                <small>{seller.stage}</small>
              </div>
              <p>{seller.whyNow[0]}</p>
              <footer>
                <span>{seller.owner.split(" · ")[0]}</span>
                <span className={slaAtRisk ? "sla-risk" : ""}>
                  {slaAtRisk ? (isZh ? "SLA 超时" : "SLA overdue") : `${seller.originRegion} → ${seller.targetRegion}`}
                </span>
              </footer>
            </div>
          </button>
          );
        })}
      </div>
      <div className="portfolio-capacity">
        <div><span>{isZh ? "本周覆盖" : "Coverage this week"}</span><strong>18 / 24</strong></div>
        <div className="capacity-track"><span /></div>
        <small>{isZh ? "假设：每位 AM 覆盖卖家数提升 30–50%" : "Pilot hypothesis: 30–50% more sellers covered per AM"}</small>
      </div>
    </aside>
  );
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`status-badge status-${status}`}>{status.replaceAll("_", " ")}</span>;
}

function EvidenceDrawer({
  evidence,
  selected,
  onSelect,
  language,
}: {
  evidence: Evidence[];
  selected?: Evidence;
  onSelect: (evidence: Evidence) => void;
  language: Language;
}) {
  const isZh = language === "zh";
  return (
    <aside className="evidence-drawer">
      <div className="panel-heading">
        <div>
          <span className="section-kicker">{isZh ? "证据" : "Evidence"}</span>
          <h3>{isZh ? "来源抽屉" : "Source drawer"}</h3>
        </div>
        <span className="count-pill">{evidence.length}</span>
      </div>
      <div className="evidence-list">
        {evidence.map((item) => (
          <button
            className={selected?.id === item.id ? "evidence-item selected" : "evidence-item"}
            type="button"
            key={item.id}
            onClick={() => onSelect(item)}
          >
            <span className={`source-key ${item.sourceType}`}>{item.sourceKey}</span>
            <span>
              <strong>{item.title}</strong>
              <small>{item.publisher} · {item.sourceType}</small>
            </span>
            <ChevronRight size={15} />
          </button>
        ))}
      </div>
      {selected && (
        <div className="evidence-detail">
          <div className="evidence-detail-meta">
            <span>{selected.sourceType === "synthetic" ? (isZh ? "合成数据" : "Synthetic data") : (isZh ? "公开来源" : "Public source")} · {selected.retrievalMode}</span>
            <span>{selected.observedAt}</span>
          </div>
          <p>{selected.excerpt}</p>
          <small>{selected.relevance}</small>
          {selected.caveat && <div className="evidence-caveat"><ShieldCheck size={12} />{selected.caveat}</div>}
          <a href={selected.url} target="_blank" rel="noreferrer">
            {isZh ? "打开公开参考" : "Open public reference"} <ExternalLink size={13} />
          </a>
        </div>
      )}
    </aside>
  );
}

function AgentContextGrid({
  context,
  language,
  syncing,
  onSync,
}: {
  context: BootstrapResponse["agentContext"];
  language: Language;
  syncing: boolean;
  onSync: () => void;
}) {
  const isZh = language === "zh";
  const lastMessage = context.communication.messages.at(-1);
  return (
    <section className="agent-context-grid">
      <article className="system-card crm-card">
        <div className="system-card-head">
          <span><span className="connector-dot" /> Salesforce-compatible CRM</span>
          <strong>{context.crmLead.syncStatus}</strong>
        </div>
        <h3>{context.crmLead.stage}</h3>
        <p>{context.crmLead.nextAction}</p>
        <footer>
          <span>{context.crmLead.externalId}</span>
          <button type="button" disabled={syncing} onClick={onSync}>
            {syncing ? <span className="spinner dark" /> : <Route size={13} />}
            {isZh ? "同步" : "Sync now"}
          </button>
        </footer>
      </article>
      <article className="system-card">
        <div className="system-card-head">
          <span><Mail size={14} /> {isZh ? "客户沟通" : "Customer communication"}</span>
          <strong>{context.communication.status.replaceAll("_", " ")}</strong>
        </div>
        <h3>{context.communication.contactName}</h3>
        <p>{lastMessage?.content ?? (isZh ? "尚无沟通记录" : "No communication recorded")}</p>
        <footer><span>{context.communication.channel}</span><span>{context.communication.messages.length} {isZh ? "条记录" : "events"}</span></footer>
      </article>
      <article className="system-card memory-card">
        <div className="system-card-head">
          <span><BrainCircuit size={14} /> {isZh ? "Agent 长期记忆" : "Agent memory"}</span>
          <strong>{context.memories.length}</strong>
        </div>
        <div className="memory-list">
          {context.memories.slice(0, 3).map((memory) => (
            <div key={memory.id}>
              <span>{memory.label}</span>
              <p>{memory.value}</p>
              <small>{Math.round(memory.confidence * 100)}% · {memory.sourceType}</small>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
}

function EmptyMission({
  account,
  agentContext,
  language,
  playbook,
  onSubmit,
  onSync,
  syncing,
  loading,
}: {
  account: SellerAccount;
  agentContext: BootstrapResponse["agentContext"];
  language: Language;
  playbook: SalesPlaybook;
  onSubmit: () => void;
  onSync: () => void;
  syncing: boolean;
  loading: boolean;
}) {
  const isZh = language === "zh";
  const playbooks: Array<{
    id: SalesPlaybook;
    icon: typeof Search;
    label: string;
    detail: string;
  }> = [
    {
      id: "qualify",
      icon: Search,
      label: isZh ? "资格判断" : "Qualify",
      detail: isZh ? "判断匹配度和下一步" : "Score fit and next step",
    },
    {
      id: "first_touch",
      icon: Mail,
      label: isZh ? "首次触达" : "First touch",
      detail: isZh ? "生成个性化双语邮件" : "Personalized bilingual outreach",
    },
    {
      id: "reactivate",
      icon: Phone,
      label: isZh ? "重新激活" : "Reactivate",
      detail: isZh ? "恢复停滞的销售机会" : "Recover a stalled opportunity",
    },
    {
      id: "launch_rescue",
      icon: Rocket,
      label: isZh ? "首发排障" : "Launch rescue",
      detail: isZh ? "协调卖家与内部团队" : "Coordinate seller and internal teams",
    },
  ];
  const selectedPlaybook =
    playbooks.find((item) => item.id === playbook) ?? playbooks[0];
  const SelectedPlaybookIcon = selectedPlaybook.icon;
  const lifecycle = lifecycleStageForAccount(account);
  return (
    <section className="sales-agent-start">
      <div className="lead-intelligence-card">
        <div className="lead-intelligence-top">
          <div className="lead-avatar">{account.name.slice(0, 2).toUpperCase()}</div>
          <div>
            <span className="section-kicker">{isZh ? "销售机会情报" : "Sales opportunity intelligence"}</span>
            <div className="lead-title-row">
              <h2>{account.name}</h2>
              <span className={`lifecycle-badge ${lifecycle.toLowerCase()}`}>
                {lifecycle}
              </span>
            </div>
            <p>{account.companySummary}</p>
          </div>
          <div className={`temperature-badge ${account.leadTemperature}`}>
            {account.leadTemperature}
          </div>
        </div>
        <div className="lead-facts">
          <div><small>{isZh ? "联系人" : "Contact"}</small><strong>{account.contactName}</strong><span>{account.contactRole}</span></div>
          <div><small>{isZh ? "来源" : "Source"}</small><strong>{account.leadSource}</strong><span>{account.stage}</span></div>
          <div><small>{isZh ? "下一里程碑" : "Next milestone"}</small><strong>{account.nextMilestone}</strong><span>{account.nextFollowUp ?? (isZh ? "尚未安排" : "Not scheduled")}</span></div>
        </div>
        <div className="lead-governance">
          <span className={`contact-policy ${account.contactability}`}>
            <ShieldCheck size={13} />
            {account.contactability.replaceAll("_", " ")}
          </span>
          <span>{account.sourceSystem}</span>
          <span>{account.assignedTeam}</span>
          <span>{isZh ? "数据时间" : "Data as of"} {new Date(account.dataFreshness).toLocaleString()}</span>
          <span>v{account.version}</span>
        </div>
        <div className="us-impact-strip">
          <div>
            <small>{isZh ? "美国站新增选品潜力" : "US selection potential"}</small>
            <strong>{account.usImpact.selectionPotential}</strong>
          </div>
          <div>
            <small>{isZh ? "首年 GMV 假设" : "First-year GMV hypothesis"}</small>
            <strong>{account.usImpact.firstYearGmvHypothesis}</strong>
          </div>
          <div>
            <small>{isZh ? "美国侧负责人" : "US-side owner"}</small>
            <strong>{account.usImpact.usOwner}</strong>
          </div>
          <span>{account.usImpact.launchWindow}</span>
        </div>
        <div className="journey-map">
          <div className="journey-heading">
            <span>{isZh ? "卖家到首单旅程" : "Seller-to-first-sale journey"}</span>
            <small>{isZh ? "跨系统、跨区域交接" : "Cross-system, cross-region handoffs"}</small>
          </div>
          <div className="journey-steps">
            {account.journeyMilestones.map((milestone) => (
              <div className={`journey-step ${milestone.status}`} key={milestone.id}>
                <i>{milestone.status === "complete" ? <Check size={12} /> : null}</i>
                <strong>{milestone.label}</strong>
                <span>{milestone.owner}</span>
                <small>{milestone.system}</small>
              </div>
            ))}
          </div>
        </div>
        <div className="signal-columns">
          <div>
            <span><Zap size={13} /> {isZh ? "购买信号" : "Buying signals"}</span>
            {account.buyingSignals.map((signal) => <p key={signal}>{signal}</p>)}
          </div>
          <div>
            <span><ShieldCheck size={13} /> {isZh ? "已知阻力" : "Known friction"}</span>
            {account.painPoints.map((painPoint) => <p key={painPoint}>{painPoint}</p>)}
          </div>
        </div>
      </div>

      <AgentContextGrid
        context={agentContext}
        language={language}
        syncing={syncing}
        onSync={onSync}
      />

      <div className="next-best-action">
        <div className="next-action-icon"><SelectedPlaybookIcon size={23} /></div>
        <div>
          <span>{isZh ? "推荐的下一销售动作" : "Recommended next sales action"}</span>
          <h3>{selectedPlaybook.label}</h3>
          <p>
            {isZh
              ? `Omar 将基于 ${account.contactName} 的购买信号和当前 CRM 阶段，完成研究、资格判断和中英文销售动作草稿。`
              : `Omar will use ${account.contactName}'s buying signals and current CRM stage to research, qualify, and draft the next bilingual sales action.`}
          </p>
        </div>
        <button
          className="button button-primary next-action-button"
          type="button"
          disabled={loading}
          onClick={onSubmit}
        >
          {loading ? <span className="spinner" /> : <Sparkles size={17} />}
          {loading
            ? (isZh ? "Agent 工作中" : "Agent working")
            : (isZh ? "运行推荐动作" : "Run recommended action")}
        </button>
      </div>

      <div className="agent-policy-note">
        <ShieldCheck size={18} />
        <div>
          <strong>{isZh ? "Agent 自动选择工作流" : "Agent-selected workflow"}</strong>
          <p>
            {isZh
              ? `根据 ${lifecycle} 阶段、CRM、客户沟通和长期记忆，Omar 已选择“${selectedPlaybook.label}”。所有外部动作仍需人工审批。`
              : `Based on the ${lifecycle} stage, CRM, customer communication, and memory, Omar selected “${selectedPlaybook.label}.” Every external action still requires approval.`}
          </p>
        </div>
      </div>
      {loading && (
        <div className="mission-progress" role="status" aria-live="polite">
          <span className="spinner dark" />
          <div>
            <strong>{isZh ? "正在读取证据并生成计划" : "Reading evidence and building the plan"}</strong>
            <small>
              {isZh
                ? "Agent 正在研究线索、判断机会并起草个性化触达。模型慢时会自动降级。"
                : "The agent is researching, qualifying, and drafting personalized outreach. Slow models fall back automatically."}
            </small>
          </div>
        </div>
      )}
      <div className="workflow-preview">
        {[
          [1, isZh ? "观察阶段与信号" : "Observe stage & signals"],
          [2, isZh ? "选择下一工作流" : "Select next workflow"],
          [3, isZh ? "生成可执行动作" : "Generate action"],
          [4, isZh ? "审批、执行与学习" : "Approve, act & learn"],
        ].map(([number, text]) => (
          <span key={String(number)}><b>{number}</b>{text}</span>
        ))}
      </div>
    </section>
  );
}

function LeadCreatePanel({
  language,
  loading,
  onClose,
  onCreate,
  onImport,
  importReport,
}: {
  language: Language;
  loading: boolean;
  onClose: () => void;
  onCreate: (input: LeadCreateRequest) => void;
  onImport: (leads: LeadCreateRequest[]) => void;
  importReport?: {
    created: number;
    duplicates: number;
    failed: number;
    results: Array<{ name: string; created: boolean; error?: string }>;
  };
}) {
  const isZh = language === "zh";
  const [mode, setMode] = useState<"single" | "bulk">("single");
  const [csv, setCsv] = useState(
    "name,category,contactName,contactRole,leadSource,buyingSignal,painPoint,owner,contactability\n",
  );
  const [form, setForm] = useState<LeadCreateRequest>({
    name: "",
    website: "",
    category: "",
    contactName: "",
    contactRole: "",
    leadSource: "",
    buyingSignal: "",
    painPoint: "",
    owner: "Lin Wei · Account Manager",
    contactability: "unknown",
  });
  const set = (key: keyof LeadCreateRequest, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const canCreate = [
    form.name,
    form.category,
    form.contactName,
    form.contactRole,
    form.leadSource,
    form.buyingSignal,
    form.painPoint,
    form.owner,
  ].every((value) => value.trim().length >= 2);
  const parsedLeads = parseLeadCsv(csv);

  return (
    <div className="lead-modal-backdrop" role="presentation">
      <section className="lead-modal" role="dialog" aria-modal="true" aria-label={isZh ? "创建销售线索" : "Create sales lead"}>
        <div className="panel-heading">
          <div>
            <span className="section-kicker">{isZh ? "新销售线索" : "New sales lead"}</span>
            <h3>{isZh ? "把真实输入交给 Agent" : "Give the agent a real input"}</h3>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p className="modal-intro">
          {isZh
            ? "这里输入的信息会持久化到 D1，成为新的销售机会，并可立即运行资格判断和个性化触达。请勿输入机密数据。"
            : "This input is persisted in D1 as a new opportunity and can immediately run qualification and personalized outreach. Do not enter confidential data."}
        </p>
        <div className="lead-mode-toggle">
          <button type="button" className={mode === "single" ? "selected" : ""} onClick={() => setMode("single")}>
            {isZh ? "单条线索" : "Single lead"}
          </button>
          <button type="button" className={mode === "bulk" ? "selected" : ""} onClick={() => setMode("bulk")}>
            {isZh ? "批量 CSV" : "Bulk CSV"}
          </button>
        </div>
        {mode === "single" ? (
        <div className="lead-form-grid">
          <label>{isZh ? "公司" : "Company"}<input value={form.name} onChange={(event) => set("name", event.target.value)} placeholder="NovaTrail Gear" /></label>
          <label>{isZh ? "品类" : "Category"}<input value={form.category} onChange={(event) => set("category", event.target.value)} placeholder="Outdoor accessories" /></label>
          <label>{isZh ? "联系人" : "Contact"}<input value={form.contactName} onChange={(event) => set("contactName", event.target.value)} placeholder="Lina Chen" /></label>
          <label>{isZh ? "职位" : "Role"}<input value={form.contactRole} onChange={(event) => set("contactRole", event.target.value)} placeholder="International Sales Director" /></label>
          <label>{isZh ? "线索来源" : "Lead source"}<input value={form.leadSource} onChange={(event) => set("leadSource", event.target.value)} placeholder="Shenzhen trade event" /></label>
          <label>{isZh ? "负责人" : "Owner"}<input value={form.owner} onChange={(event) => set("owner", event.target.value)} /></label>
          <label>
            {isZh ? "联系权限" : "Contact policy"}
            <select
              value={form.contactability}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  contactability: event.target.value as LeadCreateRequest["contactability"],
                }))
              }
            >
              <option value="business_contact">{isZh ? "业务联系人" : "Business contact"}</option>
              <option value="opted_in">{isZh ? "已明确同意" : "Explicit opt-in"}</option>
              <option value="unknown">{isZh ? "未知，需确认" : "Unknown — verify first"}</option>
              <option value="do_not_contact">{isZh ? "禁止联系" : "Do not contact"}</option>
            </select>
          </label>
          <label className="full">{isZh ? "网站（可选，仅作为记录）" : "Website (optional, stored only)"}<input value={form.website} onChange={(event) => set("website", event.target.value)} placeholder="https://example.com" /></label>
          <label className="full">{isZh ? "购买信号" : "Buying signal"}<textarea value={form.buyingSignal} onChange={(event) => set("buyingSignal", event.target.value)} placeholder={isZh ? "例如：在活动中询问美国站注册和类目需求" : "Asked about US registration and category demand at an event"} /></label>
          <label className="full">{isZh ? "已知痛点" : "Known pain point"}<textarea value={form.painPoint} onChange={(event) => set("painPoint", event.target.value)} placeholder={isZh ? "例如：不确定注册投入和首发路径" : "Unclear registration effort and launch path"} /></label>
        </div>
        ) : (
          <div className="bulk-import">
            <label>
              {isZh ? "粘贴 CSV（最多 50 条）" : "Paste CSV (up to 50 leads)"}
              <textarea value={csv} onChange={(event) => setCsv(event.target.value)} />
            </label>
            <div>
              <span>{isZh ? `已识别 ${parsedLeads.length} 条线索` : `${parsedLeads.length} leads parsed`}</span>
              <small>{isZh ? "重复公司名称会自动去重，不会创建第二条记录。" : "Duplicate normalized company names are matched instead of recreated."}</small>
            </div>
            {importReport && (
              <div className="import-report" role="status">
                <strong>
                  {isZh
                    ? `已创建 ${importReport.created}，重复 ${importReport.duplicates}，失败 ${importReport.failed}`
                    : `${importReport.created} created · ${importReport.duplicates} duplicates · ${importReport.failed} failed`}
                </strong>
                {importReport.results.map((result, index) => (
                  <span className={result.error ? "failed" : ""} key={`${result.name}-${index}`}>
                    {result.name}: {result.error ?? (result.created ? (isZh ? "已创建" : "created") : (isZh ? "重复" : "duplicate"))}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="modal-actions">
          <button className="button button-ghost" type="button" onClick={onClose}>{isZh ? "取消" : "Cancel"}</button>
          <button
            className="button button-primary"
            type="button"
            disabled={(mode === "single" ? !canCreate : parsedLeads.length === 0) || loading}
            onClick={() => mode === "single" ? onCreate(form) : onImport(parsedLeads)}
          >
            {loading ? <span className="spinner" /> : <Plus size={15} />}
            {mode === "single"
              ? (isZh ? "创建并选择线索" : "Create and select lead")
              : (isZh ? "导入并自动去重" : "Import and deduplicate")}
          </button>
        </div>
      </section>
    </div>
  );
}

function OutreachEditor({
  task,
  language,
  busy,
  onSave,
  onDirtyChange,
}: {
  task: Mission["tasks"][number];
  language: Language;
  busy: boolean;
  onSave: (
    taskId: string,
    proposal: {
      channel: "email" | "wechat" | "call";
      subject: string;
      message: string;
      messageZh: string;
      followUpDays: number;
      meetingCta: string;
    },
  ) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const isZh = language === "zh";
  const proposed =
    task.payload.proposed && typeof task.payload.proposed === "object"
      ? (task.payload.proposed as Record<string, unknown>)
      : {};
  const initial = {
    channel: (["email", "wechat", "call"].includes(String(proposed.channel))
      ? proposed.channel
      : "email") as "email" | "wechat" | "call",
    subject: String(proposed.subject ?? ""),
    message: String(proposed.message ?? ""),
    messageZh: String(proposed.messageZh ?? ""),
    followUpDays: Number(proposed.followUpDays ?? 2),
    meetingCta: String(proposed.meetingCta ?? ""),
  };
  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [task.id, task.approvalPayloadHash]);
  const changed = JSON.stringify(draft) !== JSON.stringify(initial);
  useEffect(() => onDirtyChange(changed), [changed]);

  return (
    <div className="outreach-editor">
      <div className="outreach-editor-head">
        <div>
          <span className="section-kicker">{isZh ? "销售动作工作室" : "Sales action studio"}</span>
          <strong>{isZh ? "审批前可编辑" : "Editable before approval"}</strong>
        </div>
        <span className="hash-label">{task.approvalPayloadHash?.slice(0, 10)}…</span>
      </div>
      <div className="outreach-controls">
        <label>
          {isZh ? "渠道" : "Channel"}
          <select value={draft.channel} onChange={(event) => setDraft({ ...draft, channel: event.target.value as typeof draft.channel })}>
            <option value="email">Email</option>
            <option value="wechat">WeChat</option>
            <option value="call">{isZh ? "电话任务" : "Call task"}</option>
          </select>
        </label>
        <label>
          {isZh ? "几天后跟进" : "Follow up in"}
          <input type="number" min={1} max={14} value={draft.followUpDays} onChange={(event) => setDraft({ ...draft, followUpDays: Number(event.target.value) })} />
        </label>
      </div>
      <label>{isZh ? "主题" : "Subject"}<input value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} /></label>
      <label>{isZh ? "英文信息" : "English message"}<textarea value={draft.message} onChange={(event) => setDraft({ ...draft, message: event.target.value })} /></label>
      <label>{isZh ? "中文信息" : "Chinese message"}<textarea value={draft.messageZh} onChange={(event) => setDraft({ ...draft, messageZh: event.target.value })} /></label>
      <label>{isZh ? "会议目标" : "Meeting CTA"}<input value={draft.meetingCta} onChange={(event) => setDraft({ ...draft, meetingCta: event.target.value })} /></label>
      <button className="button button-ghost save-proposal" type="button" disabled={!changed || busy} onClick={() => onSave(task.id, draft)}>
        {busy ? <span className="spinner dark" /> : <Save size={14} />}
        {isZh ? "保存并重新计算审批指纹" : "Save and refresh approval fingerprint"}
      </button>
    </div>
  );
}

function OutcomeCapture({
  language,
  busy,
  lastOutcome,
  onRecord,
}: {
  language: Language;
  busy: boolean;
  lastOutcome?: string;
  onRecord: (outcome: SalesOutcomeType, note: string) => void;
}) {
  const isZh = language === "zh";
  const [outcome, setOutcome] = useState<SalesOutcomeType>("meeting_booked");
  const [note, setNote] = useState(
    isZh ? "卖家确认了下一步，请更新里程碑和跟进计划。" : "Seller confirmed the next step; update the milestone and follow-up plan.",
  );
  return (
    <div className="outcome-capture">
      <div>
        <span className="section-kicker">{isZh ? "闭环结果" : "Close the loop"}</span>
        <h4>{isZh ? "记录卖家真实反馈" : "Record the seller outcome"}</h4>
        <p>
          {isZh
            ? "US LT 关心的不只是动作是否执行，而是卖家是否真的进入注册、首发或首单的下一阶段。"
            : "The leadership signal is not whether a task ran, but whether the seller moved toward registration, launch, or first sale."}
        </p>
      </div>
      <div className="outcome-form">
        <select value={outcome} onChange={(event) => setOutcome(event.target.value as SalesOutcomeType)}>
          <option value="replied">{isZh ? "卖家已回复" : "Seller replied"}</option>
          <option value="meeting_booked">{isZh ? "已预约会议" : "Meeting booked"}</option>
          <option value="registration_unblocked">{isZh ? "注册阻塞已解除" : "Registration unblocked"}</option>
          <option value="launch_ready">{isZh ? "已达到首发准备" : "Launch ready"}</option>
          <option value="first_sale">{isZh ? "已完成首单" : "First sale verified"}</option>
          <option value="not_now">{isZh ? "暂不推进" : "Not now / nurture"}</option>
          <option value="disqualified">{isZh ? "不符合资格" : "Disqualified"}</option>
        </select>
        <input value={note} onChange={(event) => setNote(event.target.value)} />
        <button className="button button-primary" type="button" disabled={busy || note.trim().length < 2} onClick={() => onRecord(outcome, note)}>
          {busy ? <span className="spinner" /> : <Check size={15} />}
          {isZh ? "记录并更新漏斗" : "Record and update funnel"}
        </button>
      </div>
      {lastOutcome && <span className="last-outcome">{isZh ? "最近结果" : "Latest outcome"}: {lastOutcome.replaceAll("_", " ")}</span>}
    </div>
  );
}

function CustomerConversationPanel({
  context,
  language,
  canReply,
  busy,
  assessment,
  onReply,
}: {
  context: MissionResponse["agentContext"];
  language: Language;
  canReply: boolean;
  busy: boolean;
  assessment?: MissionResponse["replyAssessment"];
  onReply: (input: {
    channel: "email" | "wechat" | "call";
    language: Language;
    message: string;
  }) => void;
}) {
  const isZh = language === "zh";
  const [channel, setChannel] = useState<"email" | "wechat" | "call">(
    context.communication.channel,
  );
  const [message, setMessage] = useState(
    isZh
      ? "谢谢，我们可以安排一次会议。请先说明需要准备哪些材料。"
      : "Thanks, we can schedule a meeting. Please clarify which materials we should prepare.",
  );
  return (
    <section className="conversation-panel">
      <div className="panel-heading">
        <div>
          <span className="section-kicker">{isZh ? "客户沟通闭环" : "Customer communication loop"}</span>
          <h3>{context.communication.contactName}</h3>
        </div>
        <span className="count-pill">{context.communication.messages.length}</span>
      </div>
      <div className="message-thread">
        {context.communication.messages.slice(-6).map((item) => (
          <div className={`message-bubble ${item.direction}`} key={item.id}>
            <small>{item.direction} · {item.channel}</small>
            <p>{item.content}</p>
            <span>{new Date(item.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        ))}
      </div>
      {canReply && (
        <div className="reply-simulator">
          <div>
            <select value={channel} onChange={(event) => setChannel(event.target.value as typeof channel)}>
              <option value="email">Email</option>
              <option value="wechat">WeChat</option>
              <option value="call">{isZh ? "电话纪要" : "Call note"}</option>
            </select>
            <span>{isZh ? "模拟客户回复" : "Simulate customer reply"}</span>
          </div>
          <textarea value={message} onChange={(event) => setMessage(event.target.value)} />
          <button className="button button-primary" type="button" disabled={busy || message.trim().length < 2} onClick={() => onReply({ channel, language, message })}>
            {busy ? <span className="spinner" /> : <BrainCircuit size={15} />}
            {isZh ? "让 Omar 理解并调整下一步" : "Let Omar interpret and adapt"}
          </button>
        </div>
      )}
      {assessment && (
        <div className="reply-assessment">
          <span>{assessment.intent.replaceAll("_", " ")} · {Math.round(assessment.confidence * 100)}%</span>
          <strong>{assessment.summary}</strong>
          <p>{assessment.nextAction}</p>
        </div>
      )}
      <div className="memory-list mission-memory">
        {context.memories.slice(0, 5).map((memory) => (
          <div key={memory.id}>
            <span>{memory.label}</span>
            <p>{memory.value}</p>
            <small>{Math.round(memory.confidence * 100)}% · {memory.sourceType}</small>
          </div>
        ))}
      </div>
    </section>
  );
}

function MissionDetail({
  data,
  language,
  busyTask,
  onApprove,
  onExecute,
  onRollback,
  onUpdateProposal,
  onRecordOutcome,
  onCustomerReply,
}: {
  data: MissionResponse;
  language: Language;
  busyTask?: string;
  onApprove: (taskId: string) => void;
  onExecute: (taskId: string) => void;
  onRollback: (taskId: string) => void;
  onUpdateProposal: Parameters<typeof OutreachEditor>[0]["onSave"];
  onRecordOutcome: (outcome: SalesOutcomeType, note: string) => void;
  onCustomerReply: Parameters<typeof CustomerConversationPanel>[0]["onReply"];
}) {
  const { account, mission } = data;
  const isZh = language === "zh";
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | undefined>(mission.evidence[0]);
  const [dirtyTaskId, setDirtyTaskId] = useState<string>();

  useEffect(() => {
    setSelectedEvidence(mission.evidence[0]);
    setDirtyTaskId(undefined);
  }, [mission.id]);

  return (
    <div className="mission-layout">
      <div className="mission-main">
        <section className="mission-status-panel">
          <div className="mission-status-top">
            <div>
              <span className="section-kicker">{isZh ? "Omar 情况简报" : "Omar situation brief"}</span>
              <h2>{mission.objective}</h2>
            </div>
            <div className="mission-state">
              <StatusBadge status={mission.status} />
              <span>{mission.modelMode === "workers-ai" ? "Workers AI" : "Deterministic demo"}</span>
            </div>
          </div>
          <p className="situation-brief">{mission.situationBrief}</p>
          <div className="agent-phase-row">
            {[
              [Check, isZh ? "触发" : "Trigger", true],
              [Check, isZh ? "观察" : "Observe", true],
              [Check, isZh ? "评估" : "Assess", true],
              [mission.status === "planned" ? Clock3 : Check, isZh ? "批准" : "Approve", mission.status !== "planned"],
              [mission.status === "executed" ? Check : Zap, isZh ? "执行" : "Act", mission.status === "executed"],
              [mission.status === "executed" ? Check : FileCheck2, isZh ? "验证" : "Verify", mission.status === "executed"],
            ].map(([Icon, label, complete]) => {
              const PhaseIcon = Icon as typeof Check;
              return (
                <div className={complete ? "agent-phase complete" : "agent-phase"} key={String(label)}>
                  <span><PhaseIcon size={14} /></span>
                  <small>{String(label)}</small>
                </div>
              );
            })}
          </div>
        </section>

        <section className="findings-section">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">{isZh ? "推理链" : "Decision dossier"}</span>
              <h3>{isZh ? "事实、推断与建议" : "Facts, inference, recommendation"}</h3>
            </div>
          </div>
          <div className="findings-list">
            {mission.findings.map((finding) => (
              <article className={`finding-card ${finding.label}`} key={finding.id}>
                <div className="finding-label">{finding.label}</div>
                <div>
                  <div className="finding-title-row">
                    <h4>{finding.title}</h4>
                    <span>{finding.confidence} · {finding.freshness}</span>
                  </div>
                  <p>{finding.detail}</p>
                  <div className="finding-footer">
                    <span><Activity size={12} /> {isZh ? "什么会改变建议：" : "What would change this:"} {finding.whatWouldChange}</span>
                    <div>
                      {finding.evidenceIds.map((evidenceId) => {
                        const item = mission.evidence.find((candidate) => candidate.id === evidenceId);
                        return item ? <button key={evidenceId} type="button" onClick={() => setSelectedEvidence(item)}>{item.sourceKey}</button> : null;
                      })}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="impact-section">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">{isZh ? "预测" : "Projected impact"}</span>
              <h3>{isZh ? "试点可衡量结果" : "Measurable pilot outcomes"}</h3>
            </div>
            <span className="forecast-label">{isZh ? "非保证预测" : "Non-guaranteed forecast"}</span>
          </div>
          <div className="impact-grid">
            {mission.projectedImpact.map((impact) => (
              <article key={impact.metric}>
                <small>{impact.metric}</small>
                <div className="impact-values">
                  <span>{impact.current}</span>
                  <ArrowRight size={14} />
                  <strong>{impact.projected}</strong>
                </div>
                <footer>
                  <span>{impact.delta}</span>
                  <span>{impact.confidence} confidence</span>
                </footer>
              </article>
            ))}
          </div>
        </section>

        <section className="plan-section">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">{isZh ? "计划" : "Prioritized plan"}</span>
              <h3>{isZh ? "按依赖关系排序的行动" : "Actions sequenced by dependency"}</h3>
            </div>
            <span className="count-pill">{mission.tasks.length}</span>
          </div>
          <div className="task-list">
            {mission.tasks.map((task) => {
              const approval = mission.approvals.find((item) => item.taskId === task.id);
              const isBusy = busyTask === task.id;
              const before =
                task.payload.before && typeof task.payload.before === "object"
                  ? (task.payload.before as Record<string, unknown>)
                  : {};
              const proposed =
                task.payload.proposed && typeof task.payload.proposed === "object"
                  ? (task.payload.proposed as Record<string, unknown>)
                  : {};
              return (
                <article className={`task-card ${task.requiresApproval ? "gated" : ""}`} key={task.id}>
                  <div className="task-sequence">{String(task.sequence).padStart(2, "0")}</div>
                  <div className="task-content">
                    <div className="task-title-row">
                      <h4>{task.title}</h4>
                      <StatusBadge status={task.status} />
                    </div>
                    <p>{task.rationale}</p>
                    <div className="task-meta">
                      {task.evidenceIds.map((evidenceId) => {
                        const item = mission.evidence.find((candidate) => candidate.id === evidenceId);
                        return item ? (
                          <button type="button" key={evidenceId} onClick={() => setSelectedEvidence(item)}>
                            {item.sourceKey} · {item.publisher}
                          </button>
                        ) : null;
                      })}
                      {task.requiresApproval && <span><LockKeyhole size={12} /> {isZh ? "人工门控" : "Human-gated"}</span>}
                      <span>{task.owner}</span>
                      <span>{task.deadline}</span>
                    </div>
                    {task.requiresApproval && (
                      <div className="action-console">
                        <div className="action-diff">
                          <div>
                            <small>{isZh ? "之前" : "Before"}</small>
                            <strong>{String(before.crmStage ?? account.stage)}</strong>
                            <span>{isZh ? "CRM 阶段" : "CRM stage"}</span>
                          </div>
                          <ArrowRight size={18} />
                          <div>
                            <small>{isZh ? "建议" : "Proposed"}</small>
                            <strong>{String(proposed.crmStage ?? "Contacted")}</strong>
                            <span>{isZh ? "触达后阶段" : "after outreach"}</span>
                          </div>
                          <div>
                            <small>{isZh ? "下一动作" : "Next action"}</small>
                            <strong>{Number(proposed.followUpDays ?? 2)}d</strong>
                            <span>{String(proposed.channel ?? "email")} · {isZh ? "可回滚" : "reversible"}</span>
                          </div>
                        </div>
                        {task.status === "awaiting_approval" && (
                          <OutreachEditor
                            task={task}
                            language={language}
                            busy={isBusy}
                            onSave={onUpdateProposal}
                            onDirtyChange={(dirty) =>
                              setDirtyTaskId(dirty ? task.id : undefined)
                            }
                          />
                        )}
                        {task.status === "awaiting_approval" && (
                          <button
                            className="button button-approval"
                            type="button"
                            onClick={() => onApprove(task.id)}
                            disabled={isBusy || dirtyTaskId === task.id}
                          >
                            {isBusy ? <span className="spinner dark" /> : <UserCheck size={16} />}
                            {dirtyTaskId === task.id
                              ? (isZh ? "请先保存编辑" : "Save edits before approval")
                              : (isZh ? "批准销售动作与 CRM 更新" : "Approve sales action & CRM update")}
                          </button>
                        )}
                        {task.status === "approved" && (
                          <button
                            className="button button-primary"
                            type="button"
                            onClick={() => onExecute(task.id)}
                            disabled={isBusy}
                          >
                            {isBusy ? <span className="spinner" /> : <Zap size={16} />}
                            {isZh ? "执行模拟销售动作" : "Execute mock sales action"}
                          </button>
                        )}
                        {task.result && (
                          <div className="execution-result">
                            <Check size={17} />
                            <div>
                              <strong>{isZh ? "模拟执行完成" : "Mock execution complete"}</strong>
                              <p>{task.result.message}</p>
                              <div className="postconditions">
                                {task.result.postconditions.map((condition) => (
                                  <span key={condition.label}><Check size={11} />{condition.label}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {task.status === "completed" && (
                          <OutcomeCapture
                            language={language}
                            busy={isBusy}
                            lastOutcome={account.lastOutcome}
                            onRecord={onRecordOutcome}
                          />
                        )}
                        {task.status === "completed" && task.reversible && (
                          <button
                            className="button button-ghost rollback-button"
                            type="button"
                            onClick={() => onRollback(task.id)}
                            disabled={isBusy}
                          >
                            {isBusy ? <span className="spinner dark" /> : <Route size={15} />}
                            {isZh ? "撤销模拟动作" : "Undo mock action"}
                          </button>
                        )}
                        {approval?.approvedBy && (
                          <small className="approval-by">
                            {isZh ? "批准人" : "Approved by"} {approval.approvedBy}
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="risk-panel">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">{isZh ? "风险" : "Risks & assumptions"}</span>
              <h3>{isZh ? "需要持续验证" : "Keep these under review"}</h3>
            </div>
          </div>
          <ul>
            {mission.risks.map((risk) => <li key={risk}>{risk}</li>)}
          </ul>
        </section>
      </div>

      <div className="mission-side">
        <CustomerConversationPanel
          context={data.agentContext}
          language={language}
          canReply={mission.tasks.some((task) => task.status === "completed")}
          busy={Boolean(busyTask)}
          assessment={data.replyAssessment}
          onReply={onCustomerReply}
        />
        <EvidenceDrawer
          evidence={mission.evidence}
          selected={selectedEvidence}
          onSelect={setSelectedEvidence}
          language={language}
        />
        <section className="audit-panel">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">{isZh ? "审计" : "Audit"}</span>
              <h3>{isZh ? "代理时间线" : "Agent timeline"}</h3>
            </div>
          </div>
          <div className="timeline">
            {mission.audit.map((event) => (
              <div className="timeline-event" key={event.id}>
                <span />
                <div>
                  <strong>{event.summary}</strong>
                  <small>{event.actor} · {new Date(event.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</small>
                </div>
              </div>
            ))}
            <div className="timeline-event">
              <span />
              <div>
                <strong>{isZh ? "证据包已锁定" : "Evidence packet locked"}</strong>
                <small>Omar retrieval policy · {mission.evidence.length} sources</small>
              </div>
            </div>
          </div>
        </section>
        <section className="account-mini">
          <span className="section-kicker">{isZh ? "销售机会" : "Sales opportunity"}</span>
          <div>
            <strong>{account.name}</strong>
            <small>{account.contactName} · {account.contactRole}</small>
          </div>
          <div className="catalog-list">
            <div><span>{isZh ? "CRM 阶段" : "CRM stage"}</span><small>{account.stage}</small></div>
            <div><span>{isZh ? "下一里程碑" : "Next milestone"}</span><small>{account.nextMilestone}</small></div>
            <div><span>{isZh ? "最后动作" : "Last action"}</span><small>{account.lastAction ?? (isZh ? "无" : "None")}</small></div>
            <div><span>{isZh ? "下次跟进" : "Next follow-up"}</span><small>{account.nextFollowUp ?? (isZh ? "未安排" : "Not scheduled")}</small></div>
          </div>
        </section>
      </div>
    </div>
  );
}

function WorkspacePage({ language }: LanguageProps) {
  const isZh = language === "zh";
  const [bootstrap, setBootstrap] = useState<BootstrapResponse>();
  const [missionData, setMissionData] = useState<MissionResponse>();
  const [objective, setObjective] = useState("");
  const [playbook, setPlaybook] = useState<SalesPlaybook>("first_touch");
  const [loading, setLoading] = useState(true);
  const [busyTask, setBusyTask] = useState<string>();
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string>();
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [importReport, setImportReport] = useState<{
    created: number;
    duplicates: number;
    failed: number;
    results: Array<{ name: string; created: boolean; error?: string }>;
  }>();

  useEffect(() => {
    if (bootstrap?.account) {
      setObjective(defaultObjective(bootstrap.account, language, playbook));
    }
  }, [language, playbook, bootstrap?.account.id]);

  useEffect(() => {
    getBootstrap()
      .then((next) => {
        const recommended = recommendedPlaybookForAccount(next.account.id);
        setBootstrap(next);
        setPlaybook(recommended);
        setObjective(defaultObjective(next.account, language, recommended));
      })
      .catch((caught: Error) => setError(caught.message))
      .finally(() => setLoading(false));
  }, []);

  async function selectAccount(accountId: string) {
    if (accountId === bootstrap?.account.id) return;
    setLoading(true);
    setError(undefined);
    setMissionData(undefined);
    try {
      const next = await getBootstrap(accountId);
      setBootstrap(next);
      const recommendedPlaybook = recommendedPlaybookForAccount(accountId);
      setPlaybook(recommendedPlaybook);
      setObjective(defaultObjective(next.account, language, recommendedPlaybook));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Account selection failed.");
    } finally {
      setLoading(false);
    }
  }

  async function syncCrm() {
    if (!bootstrap) return;
    setSyncing(true);
    setError(undefined);
    try {
      const next = await syncCrmLead(bootstrap.account.id);
      setBootstrap(next);
      if (missionData) {
        setMissionData(await getMission(missionData.mission.id));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "CRM sync failed.");
    } finally {
      setSyncing(false);
    }
  }

  async function addLead(input: LeadCreateRequest) {
    setLoading(true);
    setError(undefined);
    try {
      const next = await createLead(input);
      setBootstrap(next);
      setMissionData(undefined);
      setPlaybook("first_touch");
      setObjective(defaultObjective(next.account, language, "first_touch"));
      setShowLeadForm(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Lead creation failed.");
    } finally {
      setLoading(false);
    }
  }

  async function bulkImport(leads: LeadCreateRequest[]) {
    if (!bootstrap) return;
    setLoading(true);
    setError(undefined);
    try {
      const result = await importLeads(leads);
      setImportReport(result);
      const nextAccountId =
        result.results.find((item) => item.created)?.accountId ??
        result.results[0]?.accountId ??
        bootstrap.account.id;
      const next = await getBootstrap(nextAccountId);
      setBootstrap(next);
      setMissionData(undefined);
      setPlaybook(recommendedPlaybookForAccount(next.account.id));
      setObjective(
        defaultObjective(
          next.account,
          language,
          recommendedPlaybookForAccount(next.account.id),
        ),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Lead import failed.");
    } finally {
      setLoading(false);
    }
  }

  async function runMission() {
    if (!bootstrap) return;
    setLoading(true);
    setError(undefined);
    try {
      setMissionData(
        await createMission(bootstrap.account.id, objective, language, playbook),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Mission failed.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMission(missionId: string) {
    setLoading(true);
    setError(undefined);
    try {
      setMissionData(await getMission(missionId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Mission failed.");
    } finally {
      setLoading(false);
    }
  }

  async function approve(taskId: string) {
    if (!missionData) return;
    setBusyTask(taskId);
    setError(undefined);
    try {
      setMissionData(await approveTask(missionData.mission.id, taskId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Approval failed.");
    } finally {
      setBusyTask(undefined);
    }
  }

  async function execute(taskId: string) {
    if (!missionData) return;
    setBusyTask(taskId);
    setError(undefined);
    try {
      const result = await executeTask(missionData.mission.id, taskId);
      setMissionData(result);
      setBootstrap(await getBootstrap(result.account.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Execution failed.");
    } finally {
      setBusyTask(undefined);
    }
  }

  async function rollback(taskId: string) {
    if (!missionData) return;
    setBusyTask(taskId);
    setError(undefined);
    try {
      const result = await rollbackTask(missionData.mission.id, taskId);
      setMissionData(result);
      setBootstrap(await getBootstrap(result.account.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Rollback failed.");
    } finally {
      setBusyTask(undefined);
    }
  }

  async function saveProposal(
    taskId: string,
    proposal: Parameters<typeof updateTaskProposal>[2],
  ) {
    if (!missionData) return;
    setBusyTask(taskId);
    setError(undefined);
    try {
      setMissionData(
        await updateTaskProposal(missionData.mission.id, taskId, proposal),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Proposal update failed.");
    } finally {
      setBusyTask(undefined);
    }
  }

  async function captureOutcome(outcome: SalesOutcomeType, note: string) {
    if (!missionData) return;
    const action = missionData.mission.tasks.find((task) => task.requiresApproval);
    setBusyTask(action?.id);
    setError(undefined);
    try {
      const result = await recordSalesOutcome(
        missionData.account.id,
        missionData.mission.id,
        outcome,
        note,
      );
      setMissionData(result);
      setBootstrap(await getBootstrap(result.account.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Outcome update failed.");
    } finally {
      setBusyTask(undefined);
    }
  }

  async function processCustomerReply(input: {
    channel: "email" | "wechat" | "call";
    language: Language;
    message: string;
  }) {
    if (!missionData) return;
    const action = missionData.mission.tasks.find((task) => task.requiresApproval);
    setBusyTask(action?.id);
    setError(undefined);
    try {
      const result = await sendCustomerReply(
        missionData.account.id,
        missionData.mission.id,
        input,
      );
      setMissionData(result);
      setBootstrap(await getBootstrap(result.account.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Customer reply processing failed.");
    } finally {
      setBusyTask(undefined);
    }
  }

  if (!bootstrap && loading) {
    return (
      <main className="workspace-page">
        <div className="page-loader"><span className="spinner dark" /> Loading Omar mission control…</div>
      </main>
    );
  }

  const account = missionData?.account ?? bootstrap?.account;
  const lifecycle = account ? lifecycleStageForAccount(account) : "Acquisition";
  return (
    <main className="workspace-page">
      <section className="journey-toolbar">
        <div className="journey-toolbar-main">
          <div>
            <div className="breadcrumb">
              <span>{isZh ? "卖家旅程" : "Seller Journey"}</span>
              <ChevronRight size={13} />
              <strong>{account?.name ?? "BrightCup"}</strong>
              <span className={`lifecycle-badge ${lifecycle.toLowerCase()}`}>
                {lifecycle}
              </span>
              <span className="synthetic-badge">{isZh ? "合成" : "Synthetic"}</span>
            </div>
            <h1>{isZh ? "推进下一里程碑" : "Advance the next milestone"}</h1>
            <p>
              {account
                ? `${account.nextMilestone} · ${account.owner}`
                : (isZh ? "选择一个卖家机会" : "Select a seller opportunity")}
            </p>
          </div>
          <div className="workspace-actions">
            {missionData && (
              <button className="button button-ghost" type="button" onClick={() => setMissionData(undefined)}>
                <Sparkles size={15} /> {isZh ? "新任务" : "New mission"}
              </button>
            )}
            <button className="button button-primary" type="button" onClick={() => {
              setImportReport(undefined);
              setShowLeadForm(true);
            }}>
              <Plus size={15} /> {isZh ? "添加线索" : "Add lead"}
            </button>
            <span className="snapshot-label">
              <Clock3 size={14} /> {isZh ? "数据" : "Data"} {account?.snapshotDate}
            </span>
          </div>
        </div>
      </section>

      {error && (
        <div className="error-banner" role="alert">
          <ShieldCheck size={18} />
          <span>{error}</span>
          <button type="button" aria-label="Dismiss error" onClick={() => setError(undefined)}><X size={16} /></button>
        </div>
      )}

      <div className="workspace-body">
        {bootstrap && (
          <PortfolioRadar
            bootstrap={bootstrap}
            language={language}
            selectedId={bootstrap.account.id}
            onSelect={selectAccount}
            onAddLead={() => {
              setImportReport(undefined);
              setShowLeadForm(true);
            }}
          />
        )}
        <div className="workspace-focus">
        {!missionData ? (
          <>
            <EmptyMission
              account={bootstrap!.account}
              agentContext={bootstrap!.agentContext}
              language={language}
              playbook={playbook}
              onSubmit={runMission}
              onSync={syncCrm}
              syncing={syncing}
              loading={loading}
            />
            {bootstrap?.recentMissions.length ? (
              <section className="recent-missions">
              <div className="panel-heading">
                <div>
                  <span className="section-kicker">{isZh ? "历史" : "History"}</span>
                  <h3>{isZh ? "最近任务" : "Recent missions"}</h3>
                </div>
              </div>
              <div className="recent-list">
                {bootstrap.recentMissions.map((mission) => (
                  <button key={mission.id} type="button" onClick={() => loadMission(mission.id)}>
                    <span><StatusBadge status={mission.status} /></span>
                    <strong>{mission.objective}</strong>
                    <small>{new Date(mission.createdAt).toLocaleDateString()}</small>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
              </section>
            ) : null}
          </>
        ) : (
          <MissionDetail
            data={missionData}
            language={language}
            busyTask={busyTask}
            onApprove={approve}
            onExecute={execute}
            onRollback={rollback}
            onUpdateProposal={saveProposal}
            onRecordOutcome={captureOutcome}
            onCustomerReply={processCustomerReply}
          />
        )}
        </div>
        {showLeadForm && (
          <LeadCreatePanel
            language={language}
            loading={loading}
            onClose={() => {
              setImportReport(undefined);
              setShowLeadForm(false);
            }}
            onCreate={addLead}
            onImport={bulkImport}
            importReport={importReport}
          />
        )}
      </div>
      <div className="demo-disclosure">
        <ShieldCheck size={15} />
        {bootstrap?.demoDisclosure ?? "All operating data is synthetic and all actions use a mock connector."}
      </div>
    </main>
  );
}

function DashboardPage({ language }: LanguageProps) {
  const isZh = language === "zh";
  const stages = [
    {
      id: "acquisition",
      icon: Target,
      label: "Acquisition",
      subtitle: isZh ? "从线索到完成注册" : "Lead to registered seller",
      total: 284,
      primary: "118",
      primaryLabel: isZh ? "已完成资格判断" : "qualified sellers",
      conversion: "41.5%",
      attention: 37,
      color: "#6757e8",
      checkpoints: [
        [isZh ? "新线索" : "Lead intake", 284],
        [isZh ? "完成首次触达" : "First contact", 196],
        [isZh ? "已预约会议" : "Meeting booked", 142],
        [isZh ? "资格通过" : "Qualified", 118],
        [isZh ? "注册开始" : "Registration started", 86],
      ],
      outcomes: [
        [isZh ? "首次触达中位时间" : "Median time to first contact", "19h"],
        [isZh ? "销售覆盖 / 人" : "Sellers covered / rep", "47"],
        [isZh ? "本周 SLA 风险" : "SLA risk this week", "22"],
      ],
    },
    {
      id: "launch",
      icon: Rocket,
      label: "Launch",
      subtitle: isZh ? "从注册到首单" : "Registered seller to first sale",
      total: 163,
      primary: "74",
      primaryLabel: isZh ? "达到首发准备" : "launch-ready sellers",
      conversion: "45.4%",
      attention: 29,
      color: "#087a55",
      checkpoints: [
        [isZh ? "完成注册" : "Registered", 163],
        [isZh ? "合规准备" : "Compliance ready", 121],
        [isZh ? "选品准备" : "Selection ready", 96],
        [isZh ? "首发准备" : "Launch ready", 74],
        [isZh ? "完成首单" : "First sale", 51],
      ],
      outcomes: [
        [isZh ? "注册到首发中位天数" : "Median registration to launch", "32d"],
        [isZh ? "合规阻塞" : "Compliance blockers", "18"],
        [isZh ? "未来 30 天首发" : "Launching in 30 days", "27"],
      ],
    },
    {
      id: "growth",
      icon: TrendingUp,
      label: "Growth",
      subtitle: isZh ? "从首单到可持续增长" : "First sale to durable growth",
      total: 126,
      primary: "89",
      primaryLabel: isZh ? "健康活跃卖家" : "healthy active sellers",
      conversion: "70.6%",
      attention: 24,
      color: "#d97706",
      checkpoints: [
        [isZh ? "完成首单" : "First sale", 126],
        [isZh ? "库存健康" : "Inventory healthy", 103],
        [isZh ? "广告采用" : "Ads adopted", 82],
        [isZh ? "补货完成" : "Restocked", 67],
        [isZh ? "扩展选品/市场" : "Expanded", 38],
      ],
      outcomes: [
        [isZh ? "缺货风险" : "Restock risk", "17"],
        [isZh ? "广告增长机会" : "Ads growth opportunities", "24"],
        [isZh ? "新增选品候选" : "New-selection candidates", "31"],
      ],
    },
  ] as const;
  const maxCheckpoint = 284;

  return (
    <main className="dashboard-page">
      <section className="dashboard-hero">
        <div>
          <span className="section-kicker">{isZh ? "合成规模化组合" : "Synthetic scaled portfolio"}</span>
          <h1>{isZh ? "Seller Journey 经营看板" : "Seller Journey Dashboard"}</h1>
          <p>
            {isZh
              ? "把 573 个卖家机会按 Acquisition、Launch 和 Growth 分开运营，观察转化、阻塞和 Agent 介入带来的已验证里程碑。"
              : "Operate 573 seller opportunities across Acquisition, Launch, and Growth—tracking conversion, blockers, and verified milestones from agent-assisted interventions."}
          </p>
        </div>
        <div className="dashboard-north-star">
          <small>{isZh ? "北极星" : "North star"}</small>
          <strong>164</strong>
          <span>{isZh ? "每 100 AM 小时的已验证里程碑" : "verified milestones per 100 AM hours"}</span>
        </div>
      </section>

      <section className="dashboard-summary-grid">
        {[
          [isZh ? "活跃卖家旅程" : "Active seller journeys", "573", "+18%"],
          [isZh ? "Agent 介入中的机会" : "Agent-assisted opportunities", "211", "36.8%"],
          [isZh ? "SLA 风险" : "SLA at risk", "58", "−12%"],
          [isZh ? "本月美国站选品潜力" : "US selection potential", "326 ASINs", "$18.4M GMV"],
        ].map(([label, value, change]) => (
          <article key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
            <small>{change}</small>
          </article>
        ))}
      </section>

      <section className="lifecycle-dashboard-grid">
        {stages.map((stage) => {
          const Icon = stage.icon;
          return (
            <article className={`lifecycle-module ${stage.id}`} key={stage.id}>
              <div className="lifecycle-module-head">
                <div className="lifecycle-module-icon" style={{ background: stage.color }}>
                  <Icon size={22} />
                </div>
                <div>
                  <span className={`lifecycle-badge ${stage.id}`}>{stage.label}</span>
                  <h2>{stage.subtitle}</h2>
                </div>
                <div className="module-total"><strong>{stage.total}</strong><span>{isZh ? "卖家" : "sellers"}</span></div>
              </div>
              <div className="module-primary">
                <div><strong>{stage.primary}</strong><span>{stage.primaryLabel}</span></div>
                <div><strong>{stage.conversion}</strong><span>{isZh ? "阶段转化率" : "stage conversion"}</span></div>
                <div className="needs-attention"><strong>{stage.attention}</strong><span>{isZh ? "需要人工介入" : "need human attention"}</span></div>
              </div>
              <div className="checkpoint-funnel">
                {stage.checkpoints.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <div><i style={{ width: `${Math.max(16, (value / maxCheckpoint) * 100)}%`, background: stage.color }} /></div>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
              <div className="module-outcomes">
                {stage.outcomes.map(([label, value]) => (
                  <div key={label}><span>{label}</span><strong>{value}</strong></div>
                ))}
              </div>
            </article>
          );
        })}
      </section>

      <section className="dashboard-handoff">
        <div>
          <span className="section-kicker">{isZh ? "跨阶段交接" : "Cross-stage handoffs"}</span>
          <h2>{isZh ? "Agent 的价值在于不让卖家掉进团队之间的缝隙" : "The agent prevents sellers from falling between teams"}</h2>
        </div>
        <div className="handoff-flow">
          <span>Marketing / Sales</span><ArrowRight size={18} /><span>Registration / Compliance</span><ArrowRight size={18} /><span>US Category / Launch</span><ArrowRight size={18} /><span>AM / Ads / Supply</span>
        </div>
      </section>
    </main>
  );
}

const architectureLayers = [
  {
    icon: Globe2,
    label: "Experience",
    title: "Portfolio mission control",
    detail: "Ranked seller opportunities, bilingual decision dossiers, role-specific handoffs, approvals, and audit replay.",
    accent: "mint",
  },
  {
    icon: BrainCircuit,
    label: "Agent orchestration",
    title: "Trigger → complete state machine",
    detail: "Deterministic state transitions with optional Workers AI narrative synthesis and strict schema validation.",
    accent: "coral",
  },
  {
    icon: ShieldCheck,
    label: "Control plane",
    title: "Human agency and grounding",
    detail: "Source allowlisting, injection checks, explicit confirmations, mock-only connectors, and structured errors.",
    accent: "gold",
  },
  {
    icon: Layers3,
    label: "Data layer",
    title: "Cloudflare D1",
    detail: "Persisted CRM leads, communication threads and messages, sourced agent memories, missions, evidence, approvals, sales outcomes, and append-only audit events.",
    accent: "blue",
  },
];

function ArchitecturePage({ language }: LanguageProps) {
  const isZh = language === "zh";
  return (
    <main className="content-page">
      <section className="content-hero">
        <span className="section-kicker">{isZh ? "系统设计" : "System design"}</span>
        <h1>{isZh ? "在边缘运行的可控代理系统。" : "A governed agent system at the edge."}</h1>
        <p>
          {isZh
            ? "Omar 将推理、业务规则和执行控制分离，使系统既能利用生成式 AI，也能在模型不可用时保持可预测的业务功能。"
            : "Omar separates reasoning, domain policy, and execution control so generative intelligence can improve the experience without becoming a single point of failure."}
        </p>
      </section>
      <section className="architecture-stack">
        {architectureLayers.map((layer, index) => {
          const Icon = layer.icon;
          return (
            <article className={`architecture-layer ${layer.accent}`} key={layer.title}>
              <span className="layer-number">0{index + 1}</span>
              <div className="layer-icon"><Icon size={23} /></div>
              <div>
                <small>{layer.label}</small>
                <h2>{layer.title}</h2>
                <p>{layer.detail}</p>
              </div>
            </article>
          );
        })}
      </section>
      <section className="architecture-grid">
        <article className="diagram-card">
          <div className="panel-heading">
            <div><span className="section-kicker">Request lifecycle</span><h3>Fast path by design</h3></div>
          </div>
          <div className="flow-diagram">
            {["Validate", "Retrieve", "Plan", "Persist", "Approve", "Execute"].map((step, index) => (
              <div key={step}>
                <span>{index + 1}</span>
                <strong>{step}</strong>
                {index < 5 && <ArrowRight size={15} />}
              </div>
            ))}
          </div>
          <p>Worker CPU stays focused on validation and orchestration. D1 and Workers AI calls are network waits, while campaign execution is a constant-time mock connector write.</p>
        </article>
        <article className="guardrail-card">
          <div className="panel-heading">
            <div><span className="section-kicker">Trust boundaries</span><h3>Defense in depth</h3></div>
          </div>
          {[
            ["Input", "Zod validation + injection pattern checks"],
            ["Evidence", "Domain allowlist + live/cached/synthetic labels"],
            ["Model", "Untrusted-data prompt + JSON schema validation"],
            ["Action", "Explicit approval + exact payload SHA-256"],
            ["Result", "Postcondition verification + reversible mock audit"],
          ].map(([label, detail]) => (
            <div className="guardrail-row" key={label}><strong>{label}</strong><span>{detail}</span><Check size={14} /></div>
          ))}
        </article>
      </section>
      <section className="region-model">
        <div>
          <span className="section-kicker">{isZh ? "区域模型" : "Region model"}</span>
          <h2>{isZh ? "从中国到美国开始，为全球路径而设计。" : "Start CN → US. Design for every corridor."}</h2>
        </div>
        <div className="region-route">
          <span>CN</span><i /><span>US</span><i className="future" /><span className="muted">EU</span><i className="future" /><span className="muted">JP</span>
        </div>
        <p>
          {isZh
            ? "区域包封装语言、公开证据、合规检查点、货币和运营基准；账户和任务模型保持一致。"
            : "Region packs encapsulate language, public evidence, compliance checkpoints, currency, and operating benchmarks while the account and mission model stays stable."}
        </p>
      </section>
      <section className="benchmark-section">
        <div className="panel-heading">
          <div><span className="section-kicker">Competitive architecture</span><h3>{isZh ? "借鉴成熟范式，聚焦 AGS 空白" : "Borrow proven patterns; focus on the AGS white space"}</h3></div>
        </div>
        <div className="benchmark-grid">
          {[
            ["Alibaba", "Cross-border execution", "Specialist agents and asynchronous workflows"],
            ["Shopify", "UX & extensibility", "Proactive cards, context, skills, permissioned previews"],
            ["Amazon", "Seller operations", "Deep account, inventory, compliance, and ads context"],
            ["Salesforce Agentforce", "Agentic selling", "Agents research and engage leads, schedule work, and update CRM; reps own relationships and judgment"],
          ].map(([brand, archetype, strength]) => (
            <article key={brand}><strong>{brand}</strong><span>{archetype}</span><p>{strength}</p></article>
          ))}
        </div>
        <div className="benchmark-wedge">
          <Network size={22} />
          <div>
            <strong>Omar = seller acquisition agent with AGS semantics</strong>
            <p>Like the Agentforce transition, routine research, message drafting, CRM updates, and follow-up scheduling move to the agent. The human seller recruiter owns relationship judgment, approval, negotiation, and exceptions. Omar adds China→US registration and launch context rather than acting as a generic CRM copilot.</p>
          </div>
        </div>
      </section>
    </main>
  );
}

function EvaluationPage({ language }: LanguageProps) {
  const isZh = language === "zh";
  const evals = [
    ["Grounding precision", "100%", "Every cited key must resolve to the allowlisted evidence packet.", "live"],
    ["Approval enforcement", "100%", "Executable tasks fail closed until a persisted approval exists.", "live"],
    ["Plan completeness", "≥ 95%", "Brief, impact, risks, tasks, owners, and evidence are all present.", "target"],
    ["Action usefulness", "≥ 4.2/5", "Human raters score specificity, feasibility, and business value.", "target"],
    ["Forecast calibration", "± 20%", "Projected ranges are compared with synthetic replay outcomes.", "roadmap"],
    ["Bilingual parity", "≥ 90%", "English and Chinese outputs preserve facts, caveats, and controls.", "target"],
  ];
  return (
    <main className="content-page">
      <section className="content-hero split">
        <div>
          <span className="section-kicker">{isZh ? "评估策略" : "Evaluation strategy"}</span>
          <h1>{isZh ? "评估决策质量，而不只是文字质量。" : "Measure decision quality, not just prose quality."}</h1>
        </div>
        <p>
          {isZh
            ? "Omar 的评估体系覆盖证据、计划、控制、结果和用户信任。发布门槛优先考虑安全失败和可审计性。"
            : "Omar's eval system spans evidence, planning, controls, outcomes, and operator trust. Release gates prioritize safe failure and traceability."}
        </p>
      </section>
      <section className="kpi-tree">
        <div className="north-star">
          <small>{isZh ? "北极星指标" : "North-star outcome"}</small>
          <strong>{isZh ? "每 100 个客户经理小时新增的已验证卖家里程碑" : "Incremental verified seller milestones per 100 AM hours"}</strong>
          <TrendingUp size={25} />
        </div>
        <div className="kpi-branches">
          {[
            [Zap, isZh ? "速度" : "Speed", "Time to diagnosis", "Time to approved action"],
            [Target, isZh ? "质量" : "Quality", "Opportunity precision", "Forecast calibration"],
            [ShieldCheck, isZh ? "信任" : "Trust", "Citation coverage", "Approval compliance"],
            [BarChart3, isZh ? "结果" : "Outcome", "Revenue lift", "Hours returned"],
          ].map(([Icon, name, one, two]) => {
            const KpiIcon = Icon as typeof Zap;
            return (
              <article key={String(name)}>
                <KpiIcon size={19} />
                <strong>{String(name)}</strong>
                <span>{String(one)}</span>
                <span>{String(two)}</span>
              </article>
            );
          })}
        </div>
      </section>
      <section className="dual-kpi">
        <article>
          <span className="section-kicker">{isZh ? "Amazon → 卖家" : "Amazon → seller funnel"}</span>
          <div>{["Lead", "Qualify", "Register", "Activate", "Adopt", "Retain"].map((step) => <span key={step}>{step}</span>)}</div>
        </article>
        <article>
          <span className="section-kicker">{isZh ? "卖家 → 买家" : "Seller → buyer outcomes"}</span>
          <div>{["Selection", "Availability", "Conversion", "Sales", "ROAS", "CX"].map((step) => <span key={step}>{step}</span>)}</div>
        </article>
      </section>
      <section className="eval-table-section">
        <div className="panel-heading">
          <div><span className="section-kicker">MVP scorecard</span><h3>{isZh ? "上线门槛与目标" : "Release gates and targets"}</h3></div>
        </div>
        <div className="eval-table">
          <div className="eval-row eval-head"><span>Measure</span><span>Target</span><span>Method</span><span>Status</span></div>
          {evals.map(([name, target, method, status]) => (
            <div className="eval-row" key={name}>
              <strong>{name}</strong><strong>{target}</strong><span>{method}</span><StatusBadge status={status} />
            </div>
          ))}
        </div>
      </section>
      <section className="responsible-grid">
        <article>
          <ShieldCheck size={22} />
          <h3>{isZh ? "有帮助，但不越权" : "Helpful, never overreaching"}</h3>
          <p>{isZh ? "所有外部影响行动都需要明确的人工确认，服务端再次校验审批状态。" : "Every externally consequential action requires explicit human confirmation and a server-side approval check."}</p>
        </article>
        <article>
          <BookOpen size={22} />
          <h3>{isZh ? "证据可见" : "Evidence stays visible"}</h3>
          <p>{isZh ? "公开与合成证据清晰区分，预测范围和不确定性始终展示。" : "Public and synthetic evidence are clearly separated; forecast ranges and uncertainty remain visible."}</p>
        </article>
        <article>
          <Network size={22} />
          <h3>{isZh ? "测试分层" : "Layered evaluation"}</h3>
          <p>{isZh ? "单元测试保护策略，离线评估检查计划质量，人工评审验证实际可用性。" : "Unit tests protect policy, offline evals score plans, and human review validates real operating usefulness."}</p>
        </article>
      </section>
    </main>
  );
}

function RoadmapPage({ language }: LanguageProps) {
  const isZh = language === "zh";
  const phases = [
    {
      label: isZh ? "现在 · MVP" : "Now · MVP",
      title: isZh ? "可信任务闭环" : "Trusted mission loop",
      state: "current",
      items: isZh
        ? ["中国到美国合成账户", "广告优化任务", "人工审批与模拟执行", "中英文界面"]
        : ["Synthetic CN→US account", "Campaign optimization mission", "Approval + mock execution", "English + Chinese UX"],
    },
    {
      label: isZh ? "下一步 · 试点" : "Next · Pilot",
      title: isZh ? "经理工作台" : "Manager operating desk",
      state: "next",
      items: isZh
        ? ["多账户机会队列", "CRM 与案例管理适配器", "每周业务回顾", "受控的真实连接器"]
        : ["Multi-account opportunity queue", "CRM and case adapters", "Weekly business review", "Scoped real connectors"],
    },
    {
      label: isZh ? "之后 · 扩展" : "Later · Scale",
      title: isZh ? "全球增长网络" : "Global growth network",
      state: "future",
      items: isZh
        ? ["欧盟、日本和拉美区域包", "跨账户基准", "因果实验平台", "合作伙伴行动市场"]
        : ["EU, Japan, and LATAM packs", "Cross-account benchmarks", "Causal experiment platform", "Partner action marketplace"],
    },
  ];
  return (
    <main className="content-page">
      <section className="content-hero">
        <span className="section-kicker">{isZh ? "路线图" : "Product roadmap"}</span>
        <h1>{isZh ? "先赢得信任，再扩大自主性。" : "Earn trust before expanding autonomy."}</h1>
        <p>
          {isZh
            ? "路线图以可验证的用户价值为门槛：先证明洞察和建议有用，再逐步连接更多账户、区域和真实工作流。"
            : "Each horizon is gated by demonstrated operator value: prove the insight and plan first, then connect more accounts, regions, and real workflows."}
        </p>
      </section>
      <section className="roadmap">
        {phases.map((phase, index) => (
          <article className={`roadmap-phase ${phase.state}`} key={phase.label}>
            <div className="phase-marker"><span>{index + 1}</span></div>
            <div className="phase-content">
              <small>{phase.label}</small>
              <h2>{phase.title}</h2>
              <ul>{phase.items.map((item) => <li key={item}><Check size={14} />{item}</li>)}</ul>
            </div>
          </article>
        ))}
      </section>
      <section className="roadmap-bets">
        <div className="panel-heading">
          <div><span className="section-kicker">{isZh ? "关键赌注" : "Strategic bets"}</span><h3>{isZh ? "决定产品成败的假设" : "The assumptions that matter"}</h3></div>
        </div>
        <div className="bets-grid">
          {[
            ["01", isZh ? "经理采用" : "Manager adoption", isZh ? "把准备工作压缩到几分钟，同时保留决策权。" : "Compress preparation to minutes without taking away judgment."],
            ["02", isZh ? "证据优势" : "Evidence advantage", isZh ? "比通用助手更贴近业务上下文、更容易被信任。" : "Beat generic assistants on context, traceability, and trust."],
            ["03", isZh ? "机制复利" : "Mechanism compounding", isZh ? "每次审批和结果都改善未来建议和评估。" : "Every approval and outcome improves future plans and evaluation."],
          ].map(([number, title, detail]) => (
            <article key={number}><span>{number}</span><h3>{title}</h3><p>{detail}</p></article>
          ))}
        </div>
      </section>
    </main>
  );
}

export function App() {
  const [language, setLanguage] = useState<Language>(() => {
    const stored = window.localStorage.getItem("omar-language");
    return stored === "zh" ? "zh" : "en";
  });
  const navigate = useNavigate();
  const path = window.location.pathname;
  const onWorkspace =
    path.startsWith("/workspace") || path.startsWith("/dashboard");

  useEffect(() => {
    window.localStorage.setItem("omar-language", language);
    document.documentElement.lang = language === "zh" ? "zh-CN" : "en";
  }, [language]);

  const footerYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <div className={onWorkspace ? "app app-workspace" : "app"}>
      <Header language={language} onLanguageChange={setLanguage} />
      <Routes>
        <BrowserRoute path="/" element={<LandingPage language={language} />} />
        <BrowserRoute path="/dashboard" element={<DashboardPage language={language} />} />
        <BrowserRoute path="/workspace" element={<WorkspacePage language={language} />} />
        <BrowserRoute
          path="*"
          element={
            <main className="not-found">
              <OmarMark />
              <h1>404</h1>
              <p>This route is outside Omar's current mission map.</p>
              <button className="button button-primary" type="button" onClick={() => navigate("/")}>Return home</button>
            </main>
          }
        />
      </Routes>
      {!onWorkspace && (
        <footer className="site-footer">
          <OmarMark compact />
          <span>© {footerYear} Omar portfolio project</span>
          <span>Independent · Synthetic demo data · Public evidence only</span>
        </footer>
      )}
    </div>
  );
}
